import React from "react";
import { cn } from "@edusogno/lib/utils";
import { LoginUserItem, ModifiedEventsItem } from "@/types";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import { getTypeClass } from "@/utils/agenda.ts";
import GetIcon from "@/components/agenda/desktop/GetEventIcon.tsx";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { useMe } from "@/data/user";

// Extend dayjs with timezone and utc plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Rome");

interface EventProps {
  className?: string;
  isDeleted?: boolean;
  event: ModifiedEventsItem;
}

const EventItem: React.FC<EventProps> = (props) => {
  const { me } = useMe();
  const userTimezone = me?.item?.user.timezone || "Europe/Rome";
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Agenda", me?.item?.user as LoginUserItem),
  );

  const { className, event, isDeleted } = props;
  const {
    data,
    ora,
    ora_fine: oraFine,
    user_events: userEvents,
    type,
    titolo_argomento: topic,
    materials,
    titolo,
    nome_visualizzato: nomeVisualizzato,
  } = event;
  

  // Parse the start and end times in the "Europe/Rome" timezone
  const startDate = dayjs.tz(`${data} ${ora}`, "YYYY-MM-DD HH:mm", userTimezone);
  const endTime = oraFine
    ? dayjs.tz(`${data} ${oraFine}`, "YYYY-MM-DD HH:mm", userTimezone)
    : startDate.add(1, "hour");

  const isPast = dayjs().isAfter(endTime, "minutes");
  const isPresent = userEvents[0]?.is_logged_in;

  const { openModal } = useModalAction();
  let modifiedType = type;

  if (type.toLowerCase() === "speaking" && topic === "Mock test") {
    modifiedType = "MockTest";
  }

  const duration = endTime.diff(startDate, "minutes");
  const itemType = type?.toLowerCase();
  const customStyle = getTypeClass(
    itemType,
    nomeVisualizzato,
    isPast,
    isDeleted,
  );
  const noDesscList = ["one to one", "writing correction"];
  const headerClass = customStyle;

  const handleOnClick = () =>
    openModal(
      "AGENDA_EVENT_ITEM",
      {
        ...event,
        isPast,
        date: startDate,
        duration,
        endTime,
        materials,
      },
      {
        renderHeader: (
          <div
            className={cn(
              `w-full flex-row flex justify-between items-center ${headerClass} gap-x-10 h-fit px-4 py-3 rounded-t-md`,
              `${isPast ? "!bg-shade-one !opacity-100 !text-brand" : ""}`,
            )}
          >
            <p className={cn(`text-14px font-semibold w-1/2`)}>
              {modifiedType}
            </p>

            {(!noDesscList?.includes(modifiedType?.toLowerCase()) ||
              isPast) && (
              <div
                data-track-props={mixpanelPropsData}
                data-track-eventtype={
                  type == "Correction"
                    ? "Writing Correction"
                    : nomeVisualizzato
                    ? nomeVisualizzato
                    : titolo
                }
                className={
                  "border-black border flex flex-row flex-grow text-14px gap-1 bg-white items-center justify-center rounded-sm w-1/2"
                }
              >
                <p
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={
                    type == "Correction"
                      ? "Writing Correction"
                      : nomeVisualizzato
                      ? nomeVisualizzato
                      : titolo
                  }
                  className={cn(
                    "font-semibold text-primary text-center",
                    `${isDeleted ? "line-through" : ""}`,
                  )}
                >
                  {topic}
                </p>
              </div>
            )}
          </div>
        ),
        contentClass: "bg-white",
      },
    );

  return (
    <button
      className={cn(
        "flex flex-row items-center  relative @container px-[min(10px,3%)] rounded-lg ",
        customStyle,
        className,
      )}
      onClick={() => handleOnClick()}
    >
      <div className={"flex flex-row gap-x-2 absolute items-center"}>
        <GetIcon present={isPresent} past={isPast} deleted={isDeleted} />
        <div
          data-track-props={mixpanelPropsData}
          data-track-eventtype={
            type == "Correction"
              ? "Writing Correction"
              : nomeVisualizzato
              ? nomeVisualizzato
              : titolo
          }
          className={
            "flex flex-col text-sm   [&>p]:text-[clamp(6px,5cqmin_+_1px,10px)] [&>h5]:text-[clamp(6px,5cqmin_+_1px,10px)]  [&>p]:leading-[100%] [&>h5]:leading-[120%]"
          }
        >
          <p
            data-track-props={mixpanelPropsData}
            data-track-eventtype={
              type == "Correction"
                ? "Writing Correction"
                : nomeVisualizzato
                ? nomeVisualizzato
                : titolo
            }
            className={cn(
              "text-start line-clamp-1 !text-xs",
              `${isDeleted ? "line-through" : ""}`,
            )}
          >
            {startDate.format("HH:mm")} - {endTime.format("HH:mm")}
          </p>
          <h5
            data-track-props={mixpanelPropsData}
            data-track-eventtype={
              type == "Correction"
                ? "Writing Correction"
                : nomeVisualizzato
                ? nomeVisualizzato
                : titolo
            }
            className={cn(
              "text-start w-full  font-semibold line-clamp-1 !text-xs",
              `${isDeleted ? "line-through" : ""}`,
            )}
          >
            {type == "Correction"
              ? "Writing correction"
              : nomeVisualizzato
              ? nomeVisualizzato
              : titolo}
          </h5>
        </div>
      </div>
    </button>
  );
};

export default EventItem;